import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check } from "lucide-react";
import emailjs from '@emailjs/browser'; // Make sure to install: npm install @emailjs/browser

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    phoneNumber: "",
    email: "",
    comment: "",
  });

  const [validFields, setValidFields] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize EmailJS once the component mounts
  useEffect(() => {
    // Initialize with your user ID (public key)
    emailjs.init("wT5XuDDzU8BX_VIuR");
  }, []);

  // Validation functions
  const validateField = (name, value) => {
    let isValid = false;
    let errorMessage = "";

    switch (name) {
      case "email":
        isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
        errorMessage = "Please enter a valid email address";
        break;
      case "name":
        isValid = value.length >= 2;
        errorMessage = "Name must be at least 2 characters";
        break;
      case "comment":
        isValid = value.length <= 500;
        errorMessage = "Comment must be less than 500 characters";
        break;
      default:
        isValid = value.length >= 0;
    }

    setValidFields((prev) => ({ ...prev, [name]: isValid }));
    setErrors((prev) => ({ ...prev, [name]: isValid ? "" : errorMessage }));
    return isValid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    validateField(name, value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validations = Object.keys(formData).map((key) =>
      validateField(key, formData[key])
    );

    if (validations.every(Boolean)) {
      setIsSubmitting(true);
      setErrors({});

      try {
        // Current date and time for the email
        const now = new Date();
        const formattedDate = now.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });

        // Prepare template parameters for EmailJS
        const templateParams = {
          name: formData.name,
          email: formData.email,
          phoneNumber: formData.phoneNumber || "Not provided",
          comment: formData.comment || "No message provided",
          title: "Contact Form Submission",
          time: formattedDate
        };

        // Send the email using EmailJS directly from the browser
        const response = await emailjs.send(
          "service_i06loar", // Your service ID
          "template_u7poh3t", // Your template ID
          templateParams
        );

        // Check if the request was successful
        if (response.status === 200) {
          // Show success message
          setIsSubmitted(true);
          setTimeout(() => {
            setShowSuccessMessage(true);
          }, 500);
        } else {
          throw new Error("Failed to send message");
        }
      } catch (error) {
        console.error("Error sending message:", error);

        // Set error messages
        setErrors({
          form: "Failed to send message. Please try again later."
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const getInputClassName = (fieldName) => {
    const baseStyles = `
      w-full p-4 bg-white/90 text-gray-800 
      border rounded-md 
      focus:outline-none focus:ring-1
      transition-all duration-200
      backdrop-blur-sm
    `;

    if (formData[fieldName] && !validFields[fieldName]) {
      return `${baseStyles} border-red-400 ring-red-200 bg-red-50/90`;
    }

    if (validFields[fieldName]) {
      return `${baseStyles} border-green-400 ring-green-200 bg-green-50/90`;
    }

    return `${baseStyles} border-gray-200 focus:ring-red-500/20 focus:border-red-500`;
  };

  if (showSuccessMessage) {
    return (
      <div className="relative w-full overflow-hidden">
        {/* Full yellow background to eliminate any gaps */}
        <div className="absolute inset-0 bg-[#e9b420]" />

        {/* Diagonal elements - adjusted to ensure no gaps */}
        <div
          className="absolute top-0 right-1/3 w-[34%] h-full bg-[#e9b420]"
          style={{
            clipPath: "polygon(100% 0, 0 0, 100% 100%)",
          }}
        />
        <div className="absolute top-0 right-0 w-[34%] h-full bg-[#e9b420]" />

        {/* Text Logo at the top */}
       

        <div className="relative z-10 min-h-[60vh] flex items-center justify-center p-4">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="text-center bg-white/95 backdrop-blur-sm rounded-2xl p-8 border border-white/20 shadow-xl"
          >
            <motion.div
              className="w-28 h-28 mx-auto mb-8 bg-emerald-500 rounded-full flex items-center justify-center shadow-lg border-4 border-emerald-400"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2 }}
            >
              <Check className="w-14 h-14 text-white drop-shadow-md" />
            </motion.div>
            <motion.h2
              className="text-4xl font-extrabold mb-4 text-black"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              Thank You!
            </motion.h2>
            <motion.p
              className="text-xl font-semibold text-black"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              We'll get back to you as soon as possible.
            </motion.p>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full overflow-hidden">
      {/* Main background with diagonal split like homepage */}
      <div className="absolute inset-0 bg-[#e9b320]" />
      <div
        className="absolute top-1/4 right-0 w-full h-2/3 bg-[#a41e25]"
        style={{
          clipPath: "polygon(0 30%, 100% 53%, 100% 85%, 0 60%)",
        }}
      />

    

      {/* Content */}
      <div className="relative z-10 container mx-auto flex justify-center px-4 py-12 pt-36">
        <div className="w-full max-w-2xl">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="[text-shadow:2px_2px_0_#4d1312,4px_4px_0_#4d1312] text-8xl text-white text-center mb-12"
            style={{
              fontFamily: "Headlined, sans-serif",
            }}
          >
            Contact Us
          </motion.h1>

          <AnimatePresence>
            {!isSubmitted && (
              <>
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="bg-white backdrop-blur-sm rounded-lg p-8 relative border border-white/10 shadow-xl"
                >
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="relative">
                        <input
                          type="text"
                          name="name"
                          placeholder="Full Name *"
                          value={formData.name}
                          onChange={handleChange}
                          className={getInputClassName("name")}
                          required
                        />
                        {validFields.name && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2 bg-green-500 rounded-full p-1">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        )}
                        {errors.name && (
                          <p className="text-red-300 text-sm mt-1">
                            {errors.name}
                          </p>
                        )}
                      </div>

                      <div className="relative">
                        <input
                          type="tel"
                          name="phoneNumber"
                          placeholder="Phone Number "
                          value={formData.phoneNumber}
                          onChange={handleChange}
                          className={getInputClassName("phoneNumber")}
                        />
                        {validFields.phoneNumber && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2 bg-green-500 rounded-full p-1">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        )}
                        {errors.phoneNumber && (
                          <p className="text-red-300 text-sm mt-1">
                            {errors.phoneNumber}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="relative">
                      <input
                        type="email"
                        name="email"
                        placeholder="Email *"
                        value={formData.email}
                        onChange={handleChange}
                        className={getInputClassName("email")}
                        required
                      />
                      {validFields.email && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 bg-green-500 rounded-full p-1">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                      )}
                      {errors.email && (
                        <p className="text-red-300 text-sm mt-1">
                          {errors.email}
                        </p>
                      )}
                    </div>

                    <textarea
                      name="comment"
                      placeholder="Comment"
                      rows="5"
                      value={formData.comment}
                      onChange={handleChange}
                      className={getInputClassName("comment")}
                    />

                    {errors.form && (
                      <p className="text-red-500 text-sm">{errors.form}</p>
                    )}

                    <motion.button
                      type="submit"
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                      className="w-full py-4 bg-[#a41e25] text-white rounded-md text-lg font-semibold transition-all focus:ring-2 focus:ring-[#FFD700]/50"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Sending..." : "Send Message"}
                    </motion.button>
                  </form>
                </motion.div>
                <p className="text-lg font-bold text-center text-black px-7 mt-4">
                  If you have bought our product on Amazon and you're not
                  satisfied,{" "}
                  <a
                    href="/satisfaction-guaranteed"
                    rel="noreferrer"
                    className="text-red-600  hover:underline transition-colors duration-300 hover:scale-105 hover:font-semibold"
                  >
                    Click Here
                  </a>{" "}
                   to be directed to our Satisfaction Guaranteed page.
                </p>
              </>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;